import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { RequestDto } from 'src/portal/ai-proxy-api/models/request-dto';
import { RoleEnum } from 'src/portal/ai-proxy-api/models/role-enum';
import { ProxyService } from 'src/portal/ai-proxy-api/services/proxy.service';

export enum GeminiSchemaType {
  Profiles = 'profiles',
  Companies = 'companies',
  Prompts = 'prompts',
  Skills = 'skills',
  Products = 'products',
  Domains = 'domains',
  DomainNameCompaniesUsingProductResponseSchema = 'DomainNameCompaniesUsingProductResponseSchema',
  DomainNameProductsUsingCompanyResponseSchema = 'DomainNameProductsUsingCompanyResponseSchema',
  CompanyName = 'CompanyName',
  csvProducts = 'CsvProducts',
  csvCompanies = 'CsvCompanies',
}
@Injectable({
  providedIn: 'root',
})
export class ProxyAiResponseSchemaService {
  
  private readonly defaultModelName = 'gemini-flash-latest';
  private companiesResponseSchema = {
    properties: {
      source: {
        type: 'ARRAY',
        items: {
          type: 'OBJECT',
          required: [
            'Company Name',
            'Product Name',
            'Industry',
            'Country/Region',
            'How Product Is Used',
            'Source',
            'Probability',
          ],
          properties: {
            'Company Name': {
              type: 'STRING',
            },
            'Product Name': {
              type: 'STRING',
            },
            Industry: {
              type: 'STRING',
            },
            'Country/Region': {
              type: 'STRING',
            },
            'How Product Is Used': {
              type: 'STRING',
            },
            Scope: {
              type: 'STRING',
            },
            Source: {
              type: 'STRING',
            },
            Probability: {
              type: 'STRING',
            },
            dateFirstVerified: {
              type: 'STRING',
            },
            dateLastVerified: {
              type: 'STRING',
            },
          },
        },
      },
    },
    required: ['source'],
    type: 'OBJECT',
  };

  private profilesResponseSchema = {
    properties: {
      source: {
        type: 'ARRAY',
        items: {
          type: 'OBJECT',
          required: ['Name', 'Title', 'Source'],
          properties: {
            Name: {
              type: 'STRING',
            },
            Title: {
              type: 'STRING',
            },
            Department: {
              type: 'STRING',
            },
            Level: {
              type: 'STRING',
            },
            Location: {
              type: 'STRING',
            },
            'Core Skills': {
              type: 'STRING',
            },
            Source: {
              type: 'STRING',
            },
          },
        },
      },
    },
    required: ['source'],
    type: 'OBJECT',
  };

  private promptsResponseSchema = {
    properties: {
      prompts: {
        type: 'ARRAY',
        items: {
          type: 'STRING',
        },
      },
    },
    required: ['prompts'],
    type: 'OBJECT',
  };

  private skillsResponseSchema = {
    type: 'object',
    properties: {
      skills: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            skillName: {
              type: 'string',
            },
            userRate: {
              type: 'integer',
            },
            // "userRateDescription": {
            //   "type": "string"
            // },
            companyRate: {
              type: 'integer',
            },
            // "companyRateDescription": {
            //   "type": "string"
            // }
          },
          required: ['skillName', 'userRate', 'companyRate'],
        },
      },
    },
    required: ['skills'],
  };

  private productsResponseSchema = {
    type: 'OBJECT',
    required: ['product'],
    properties: {
      product: {
        type: 'OBJECT',
        required: [
          'name',
          'description',
          'avgRating',
          'reviewCount',
          'starRating',
          'mainCategory',
          'vendorName',
          'vendorDomain',
        ],
        properties: {
          name: { type: 'STRING' },
          description: { type: 'STRING' },
          avgRating: { type: 'NUMBER' },
          reviewCount: { type: 'NUMBER' },
          starRating: { type: 'NUMBER' },
          imageUrl: { type: 'STRING' },
          productUrl: { type: 'STRING' },
          publicDetailUrl: { type: 'STRING' },
          features: { type: 'ARRAY', items: { type: 'STRING' } },
          languagesSupported: { type: 'ARRAY', items: { type: 'STRING' } },
          mainCategory: {
            type: 'OBJECT',
            properties: {
              id: { type: 'STRING' },
              name: { type: 'STRING' },
            },
            required: ['id', 'name'],
          },
          relatedCategories: {
            type: 'ARRAY',
            items: {
              type: 'OBJECT',
              properties: {
                id: { type: 'STRING' },
                name: { type: 'STRING' },
              },
            },
          },
          vendorName: { type: 'STRING' },
          vendorDomain: { type: 'STRING' },
        },
      },
    },
  };

  private domainNameResponseSchema = {
    type: 'OBJECT',
    properties: {
      items: {
        type: 'ARRAY',
        items: {
          type: 'OBJECT',
          properties: {
            ProductName: {
              type: 'STRING',
            },
            ProductVendorDomainName: {
              type: 'STRING',
            },
            CompanyDomainName: {
              type: 'STRING',
            },
          },
          propertyOrdering: [
            'ProductName',
            'ProductVendorDomainName',
            'CompanyDomainName',
          ],
          required: [
            'ProductName',
            'ProductVendorDomainName',
            'CompanyDomainName',
          ],
        },
      },
    },
    propertyOrdering: ['items'],
    required: ['items'],
  };

  private functionDeclarations = {
    functionDeclarations: [
      {
        name: 'switchChat',
        description: 'Switches the chat to the desired operating mode',
        parameters: {
          type: 'object',
          required: ['mode'],
          properties: {
            mode: {
              type: 'string',
              enum: [
                'recommendSoftware',
                'findCustomersOfProduct',
                'analyzeTechStacksForCompanies',
                'benchmarkCompanies',
                'productAnalysis',
                'findPeopleProfiles',
                'generalTechnology',
              ],
            },
            userPromt: {
              type: 'string',
            },
          },
        },
      },
    ],
  };

  private domainNameCompaniesUsingProductResponseSchema = {
    type: 'OBJECT',
    properties: {
      ProductName: {
        type: 'STRING',
      },
      ProductVendorDomainName: {
        type: 'STRING',
      },
      items: {
        type: 'ARRAY',
        items: {
          type: 'OBJECT',
          properties: {
            CompanyName: {
              type: 'STRING',
            },
            CompanyDomainName: {
              type: 'STRING',
            },
            Country: {
              type: 'STRING',
            },
            Industry: {
              type: 'STRING',
            },
            Probability: {
              type: 'STRING',
            },
            Source: {
              type: 'STRING',
            },
            dateFirstVerified: {
              type: 'STRING',
            },
            dateLastVerified: {
              type: 'STRING',
            },
          },
          propertyOrdering: [
            'CompanyName',
            'CompanyDomainName',
            'Country',
            'Industry',
            'Probability',
            'Source',
            'dateFirstVerified',
            'dateLastVerified',
          ],
          required: [
            'CompanyName',
            'CompanyDomainName',
            'Country',
            'Industry',
            'Probability',
            'Source',
            'dateFirstVerified',
            'dateLastVerified',
          ],
        },
      },
    },
    propertyOrdering: ['ProductName', 'ProductVendorDomainName', 'items'],
    required: ['ProductName', 'ProductVendorDomainName', 'items'],
  };

  private domainNameProductsUsingCompanyResponseSchema = {
    type: 'OBJECT',
    properties: {
      CompanyName: {
        type: 'STRING',
      },
      CompanyDomainName: {
        type: 'STRING',
      },
      items: {
        type: 'ARRAY',
        items: {
          type: 'OBJECT',
          properties: {
            ProductName: {
              type: 'STRING',
            },
            ProductVendorDomainName: {
              type: 'STRING',
            },
            Country: {
              type: 'STRING',
            },
            Industry: {
              type: 'STRING',
            },
            Probability: {
              type: 'STRING',
            },
            Source: {
              type: 'STRING',
            },
            dateFirstVerified: {
              type: 'STRING',
            },
            dateLastVerified: {
              type: 'STRING',
            },
          },
          propertyOrdering: [
            'ProductName',
            'ProductVendorDomainName',
            'Country',
            'Industry',
            'Probability',
            'Source',
            'dateFirstVerified',
            'dateLastVerified',
          ],
          required: [
            'ProductName',
            'ProductVendorDomainName',
            'Country',
            'Industry',
            'Probability',
            'Source',
            'dateFirstVerified',
            'dateLastVerified',
          ],
        },
      },
    },
    propertyOrdering: ['CompanyName', 'CompanyDomainName', 'items'],
    required: ['CompanyName', 'CompanyDomainName', 'items'],
  };

    private csvProductsResponseSchema = {
    properties: {
      products: {
        type: 'ARRAY',
        items: {
          type: 'OBJECT',
          required: [
            'Company',
            'CompanyDomain',
            'Country',
            'Vendor',
            'VendorDomain',
            'Product',
            'Source',
            'HitCount',
            'DateFirstVerified',
            'DateLastVerified',
          ],
          properties: {
            Company: {type: 'STRING',},
            CompanyDomain: {type: 'STRING',},
            Product: {type: 'STRING',},
            Vendor: {type: 'STRING',},
            VendorDomain: {type: 'STRING',},
            Country: {type: 'STRING',},
            Source: {type: 'STRING',},
            HitCount:{type:'NUMBER'},
            DateFirstVerified: {type: 'STRING',},
            DateLastVerified: {type: 'STRING',},
          },
        },
      },
    },
    required: ['products'],
    type: 'OBJECT',
  };

  private csvCompaniesResponseSchema = {
    type: 'OBJECT',
    properties: {
      companies: {
        type: 'ARRAY',
        items: {
          type: 'OBJECT',
          properties: {
            Company: {type: 'STRING'},
            CompanyDomain: {type: 'STRING'},
            Product: {type: 'STRING'},
            Vendor: {type: 'STRING'},
            VendorDomain: {type: 'STRING'},
            HitCount: {type: 'NUMBER'},
            Country: {type: 'STRING'},
            Scope: {type: 'STRING'},
            Source: {type: 'STRING'},
            Probability: {type: 'STRING'},
            DateFirstVerified: {type: 'STRING'},
            DateLastVerified: {type: 'STRING'},
          },
          propertyOrdering: [
            'Company',
            'CompanyDomain',
            'Product',
            'Vendor',
            'VendorDomain',
            'HitCount',
            'Country',
            'Scope',
            'Source',
            'Probability',
            'DateFirstVerified',
            'DateLastVerified',
          ],
          required: [
            'Company',
            'CompanyDomain',
            'Product',
            'Vendor',
            'VendorDomain',
            'HitCount',
            'Country',
            'Scope',
            'Source',
            'Probability',
            'DateFirstVerified',
            'DateLastVerified',
          ],
        },
      },
    },
    propertyOrdering: ['companies'],
    required: ['companies'],
  };

  // private csvCompaniesResponseSchema = {
  //   "type": "object",
  //   "properties": {
  //     "companies": {
  //       "type": "array",
  //       "items": {
  //         "type": "object",
  //         "properties": {
  //           "company": {
  //             "type": "string"
  //           },
  //           "companyDomain": {
  //             "type": "string"
  //           },
  //           "product": {
  //             "type": "string"
  //           },
  //           "vendor": {
  //             "type": "string"
  //           },
  //           "mainCategory": {
  //             "type": "string"
  //           },
  //           "parentCategory": {
  //             "type": "string"
  //           },
  //           "dateFirstVerified": {
  //             "type": "string"
  //           },
  //           "dateLastVerified": {
  //             "type": "string"
  //           }
  //         },
  //         "propertyOrdering": [
  //           "company",
  //           "companyDomain",
  //           "product",
  //           "vendor",
  //           "mainCategory",
  //           "parentCategory",
  //           "dateFirstVerified",
  //           "dateLastVerified"
  //         ]
  //       }
  //     }
  //   },
  //   "propertyOrdering": [
  //     "companies"
  //   ]
  // }
  private companyName = {
    type: 'OBJECT',
    required: ['shortCompanyName'],
    properties: {
      shortCompanyName: {
        type: 'STRING',
      },
    },
  };

  constructor(private proxyService: ProxyService) {
    
  }

  async analyzePrompt(
    systemInstruction: string,
    userPrompt: string,
    schemaType: GeminiSchemaType,
    _thinkingBudget: number = 0,
    model?: string,
    sessionId?: string
  ): Promise<any> {
    const requestBody: RequestDto = {
      config: {
        systemInstruction,
        temperature: 0.7,
        responseJsonSchema: JSON.stringify(this.getSchemaByType(schemaType)),
        // tool
      },
      contents: [
        {
          role: RoleEnum.USER,
          parts: [userPrompt],
        },
      ],
    };
    if (model) {
      (requestBody as any).model = model;
    }
    if (sessionId) {
        requestBody.sessionId = sessionId;
    }
    const blob = await firstValueFrom(
      this.proxyService.proxyRequest({ body: requestBody })
    );
    const rawText = await blob.text();
    const cleaned = rawText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.replace(/^data:\s?/, ''))
      .join('');
    try {
      const parsed = JSON.parse(cleaned);
      return parsed;
    } catch (err) {
      console.error('Error parsing JSON from SSE:', err);
      throw err;
    }

    // const geminiResponse = await this.ai.models.generateContent({
    //   model: model ?? this.defaultModelName,
    //   config: {
    //     thinkingConfig: { thinkingBudget:-1 },
    //     responseMimeType: 'application/json',
    //     responseSchema: this.getSchemaByType(schemaType),
    //     systemInstruction: [{ text: systemInstruction }],
    //      tools: [{
    //       googleSearch: {} 
    //     }],
    //   },
    //   contents: [
    //     {
    //       role: 'user',
    //       parts: [{ text: userPrompt }],
    //     },
    //   ],
    // });

    // try {
    //   const rawText = geminiResponse.text;
    //   if (!rawText) {
    //     throw new Error('Gemini response is empty');
    //   }
    //   return JSON.parse(rawText);
    // } catch (err) {
    //   console.error('Error parsing Gemini response:', err);
    //   throw err;
    // }
  }
  private getSchemaByType(schemaType: GeminiSchemaType) {
    switch (schemaType) {
      case GeminiSchemaType.Profiles:
        return this.profilesResponseSchema;
      case GeminiSchemaType.Companies:
        return this.companiesResponseSchema;
      case GeminiSchemaType.Prompts:
        return this.promptsResponseSchema;
      case GeminiSchemaType.Skills:
        return this.skillsResponseSchema;
      case GeminiSchemaType.Products:
        return this.productsResponseSchema;
      case GeminiSchemaType.Domains:
        return this.domainNameResponseSchema;
      case GeminiSchemaType.DomainNameCompaniesUsingProductResponseSchema:
        return this.domainNameCompaniesUsingProductResponseSchema;
      case GeminiSchemaType.DomainNameProductsUsingCompanyResponseSchema:
        return this.domainNameProductsUsingCompanyResponseSchema;
      case GeminiSchemaType.CompanyName:
        return this.companyName;
      case GeminiSchemaType.csvProducts:
        return this.csvProductsResponseSchema;
      case GeminiSchemaType.csvCompanies:
        return this.csvCompaniesResponseSchema;
      default:
        throw new Error(`Unsupported schema type: ${schemaType}`);
    }
  }

  public getResponseSchema(schemaType: GeminiSchemaType) {
    return this.getSchemaByType(schemaType);
  }
}
